<template>
  <div class="app-tabs" :class="[`app-tabs--${variant}`, `app-tabs--${size}`, { 'app-tabs--fill': fill }]" v-bind="$attrs" v-on="$listeners">
    <nav class="app-tabs__nav" role="tablist">
      <button
        v-for="(tab, index) in tabs"
        :key="tab._uid"
        :id="tab.tabId"
        class="app-tabs__tab"
        :class="{
          'app-tabs__tab--active': index === activeIndex,
          'app-tabs__tab--disabled': tab.disabled
        }"
        role="tab"
        :aria-selected="index === activeIndex ? 'true' : 'false'"
        :aria-disabled="tab.disabled ? 'true' : 'false'"
        :tabindex="index === activeIndex ? 0 : -1"
        :disabled="tab.disabled"
        type="button"
        @click="selectTab(index)"
      >
        <i v-if="tab.icon" :class="tab.icon" class="app-tabs__tab-icon"></i>
        <span class="app-tabs__tab-title">{{ tab.title }}</span>
      </button>
    </nav>

    <div class="app-tabs__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppTabs',
  inheritAttrs: false,
  provide () {
    return {
      tabsProvider: this
    }
  },
  props: {
    value: {
      type: [Number, String],
      default: 0
    },
    variant: {
      type: String,
      default: 'default',
      validator (val) {
        return ['default', 'pills', 'underline'].includes(val)
      }
    },
    fill: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'medium',
      validator (val) {
        return ['small', 'medium', 'large'].includes(val)
      }
    }
  },
  data () {
    return {
      tabs: [],
      activeIndex: 0
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        this.syncActiveIndex(val)
      }
    }
  },
  methods: {
    register (tab) {
      this.tabs.push(tab)
      if (this.value !== undefined && this.value !== null) {
        this.syncActiveIndex(this.value)
      }
    },
    unregister (tab) {
      const idx = this.tabs.indexOf(tab)
      if (idx !== -1) {
        this.tabs.splice(idx, 1)
      }
    },
    syncActiveIndex (val) {
      if (typeof val === 'number' && val < this.tabs.length) {
        this.activeIndex = parseInt(val)
      } else if (typeof val === 'string') {
        const idx = this.tabs.findIndex(t => t.name === val)
        if (idx !== -1) {
          this.activeIndex = idx
        }
      }
    },
    selectTab (index) {
      if (this.tabs[index] && !this.tabs[index].disabled) {
        this.activeIndex = index
        const val = this.tabs[index].name !== null
          ? this.tabs[index].name
          : index
        this.$emit('input', val)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.app-tabs {
  display: flex;
  flex-direction: column;
}

.app-tabs__nav {
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--app-border-color, #dee2e6);
  margin-bottom: 1rem;
}

.app-tabs__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--app-text-color, #656565);
  font-size: var(--app-font-size-base, 0.875rem);
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease-in-out, border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
  margin-bottom: -1px;
  white-space: nowrap;

  &:hover:not(:disabled):not(.app-tabs__tab--active) {
    color: var(--app-primary, #0F2835);
    border-bottom-color: var(--app-border-color, #dee2e6);
  }

  &:focus {
    outline: 0;
    box-shadow: 0 0 0 0.2rem var(--app-primary-focus, rgba(15, 40, 53, 0.25));
  }

  &--active {
    color: var(--app-primary, #0F2835);
    border-bottom-color: var(--app-primary, #0F2835);
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.app-tabs__tab-icon {
  font-size: 0.875rem;
}

.app-tabs__content {
  flex: 1;
}

/* Modifier: size */
.app-tabs--small {
  .app-tabs__tab {
    padding: 0.25rem 0.75rem;
    font-size: 0.75rem;
  }
}

.app-tabs--large {
  .app-tabs__tab {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
}

/* Variant: pills */
.app-tabs--pills {
  .app-tabs__nav {
    border-bottom: none;
    gap: 0.25rem;
    margin-bottom: 0;
  }

  .app-tabs__tab {
    border: 1px solid transparent;
    border-radius: var(--app-border-radius, 0.25rem);
    margin-bottom: 0;
    padding: 0.5rem 1rem;

    &:hover:not(:disabled):not(.app-tabs__tab--active) {
      border-color: var(--app-border-color, #dee2e6);
      border-bottom-color: var(--app-border-color, #dee2e6);
      background-color: var(--app-light, #F8F7F7);
    }

    &--active {
      color: #fff;
      background-color: var(--app-primary, #0F2835);
      border-color: var(--app-primary, #0F2835);
    }
  }
}

/* Modifier: fill */
.app-tabs--fill {
  .app-tabs__nav {
    flex-wrap: nowrap;
  }

  .app-tabs__tab {
    flex: 1 1 0;
    justify-content: center;
    text-align: center;
  }
}

/* Variant: underline */
.app-tabs--underline {
  .app-tabs__nav {
    border-bottom: 1px solid var(--app-border-color, #dee2e6);
    gap: 0;
  }

  .app-tabs__tab {
    padding: 0.75rem 1.25rem;
    border-bottom: 3px solid transparent;
    margin-bottom: -1px;
    font-weight: 600;

    &--active {
      border-bottom-color: var(--app-secondary, #D5DD76);
      color: var(--app-primary, #0F2835);
    }
  }
}
</style>
